import './styles.css';
import { fetchAllSnapshots, fetchMeta, getDataBaseUrl } from './api';
import type {
  CommentItem,
  CommentsSnapshot,
  MetaSnapshot,
  SearchIndexSnapshot,
  TimelineSnapshot,
  TrendsSnapshot,
  TrendTopic
} from './types';

type Tab = 'timeline' | 'trends' | 'all';

interface AppState {
  activeTab: Tab;
  query: string;
  searchDraft: string;
  loading: boolean;
  error: string | null;
  meta: MetaSnapshot | null;
  timeline: TimelineSnapshot | null;
  trends: TrendsSnapshot | null;
  comments: CommentsSnapshot | null;
  searchIndex: SearchIndexSnapshot | null;
  pendingMeta: MetaSnapshot | null;
  mobileMenuOpen: boolean;
  expandedReplyIds: Set<string>;
}

const appElement = document.querySelector<HTMLDivElement>('#app');
if (!appElement) throw new Error('#app not found');
const app = appElement;

function resolvePollMs(): number {
  const parsed = Number(import.meta.env.VITE_POLL_MS ?? 30_000);
  if (!Number.isFinite(parsed) || parsed <= 0) return 30_000;
  return Math.max(5_000, parsed);
}

const POLL_MS = resolvePollMs();
const YAJUTTER_APP_ICON_SRC = '/icons/yaju.jpg';


const state: AppState = {
  activeTab: 'timeline',
  query: '',
  searchDraft: '',
  loading: false,
  error: null,
  meta: null,
  timeline: null,
  trends: null,
  comments: null,
  searchIndex: null,
  pendingMeta: null,
  mobileMenuOpen: false,
  expandedReplyIds: new Set<string>()
};

let queryCompositionActive = false;

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function normalizeQuery(value: string): string {
  return value.normalize('NFKC').trim().toLowerCase();
}

function commentSearchText(item: CommentItem): string {
  const repliesText = (item.replies ?? []).map((reply) => `${reply.authorName} ${reply.text}`).join(' ');
  return `${item.authorName} ${item.text} ${(item.topics ?? []).join(' ')} ${repliesText}`;
}

function formatTime(value: string | null | undefined): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('ja-JP', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).format(date);
}

function formatCompactNumber(value: number): string {
  if (!Number.isFinite(value)) return '0';
  if (value >= 100_000_000) return `${(value / 100_000_000).toFixed(1)}億`;
  if (value >= 10_000) return `${(value / 10_000).toFixed(1)}万`;
  return Math.max(0, Math.round(value)).toLocaleString('ja-JP');
}

function relativeTime(value: string): string {
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return formatTime(value);
  const diffSeconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  if (diffSeconds < 60) return `${diffSeconds}秒`;
  if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}分`;
  if (diffSeconds < 86_400) return `${Math.floor(diffSeconds / 3600)}時間`;
  return `${Math.floor(diffSeconds / 86_400)}日`;
}

function modeLabel(meta: MetaSnapshot | null): string {
  if (!meta) return '読込中';
  switch (meta.mode) {
    case 'NORMAL': return '通常';
    case 'ACTIVE': return '活発';
    case 'SPIKE': return 'スパイク';
    case 'OVERFLOW': return '高負荷';
    case 'QUOTA_SAVE': return '節約';
    case 'MOCK': return 'デモ';
  }
}

function modeClass(meta: MetaSnapshot | null): string {
  if (!meta) return 'mode-loading';
  return `mode-${meta.mode.toLowerCase()}`;
}

function buildPseudoHandle(authorName: string): string {
  const normalized = authorName
    .normalize('NFKC')
    .replace(/[\s　]+/g, '')
    .replace(/[^\p{L}\p{N}_]/gu, '')
    .toLowerCase();
  const base = normalized || 'yajutter_user';
  return `@${base.slice(0, 15)}`;
}

function highlight(text: string, query: string): string {
  const safe = escapeHtml(text);
  const normalizedQuery = normalizeQuery(query);
  if (!normalizedQuery) return safe;
  const words = normalizedQuery.split(/\s+/).filter(Boolean).slice(0, 4);
  let result = safe;
  for (const word of words) {
    const escapedWord = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    result = result.replace(new RegExp(`(${escapedWord})`, 'ig'), '<mark>$1</mark>');
  }
  return result;
}

function renderAppIcon(className: string, label = 'ヤジュッター'): string {
  return `
    <img
      class="${className}"
      src="${YAJUTTER_APP_ICON_SRC}"
      alt="${escapeHtml(label)}"
      loading="eager"
      decoding="async"
    />
  `;
}

function renderLogo(): string {
  return `
    <a class="brand" href="#" data-tab="timeline" aria-label="ヤジュッター ホーム">
      ${renderAppIcon('brand-icon', 'ヤジュッター アイコン')}
      <span class="brand-text">ヤジュッター</span>
    </a>
  `;
}

function renderLeftRail(): string {
  const navItems: Array<{ id: Tab; label: string; icon: string }> = [
    { id: 'timeline', label: 'ホーム', icon: '⌂' },
    { id: 'trends', label: 'トレンド', icon: '#' },
    { id: 'all', label: 'すべて', icon: '◎' }
  ];

  return `
    <aside class="left-rail" aria-label="navigation">
      ${renderLogo()}
      <nav class="nav-list">
        ${navItems.map((item) => `
          <button class="nav-item ${state.activeTab === item.id ? 'active' : ''}" data-tab="${item.id}">
            <span class="nav-icon" aria-hidden="true">${item.icon}</span>
            <span>${item.label}</span>
          </button>
        `).join('')}
      </nav>
      <a class="tweet-button" href="${escapeHtml(state.meta?.videoUrl ?? '#')}" ${state.meta ? 'target="_blank" rel="noreferrer"' : 'aria-disabled="true"'}>
        YouTubeで見る
      </a>
    </aside>
  `;
}

function renderTopTabs(): string {
  const tabs: Array<{ id: Tab; label: string }> = [
    { id: 'timeline', label: 'タイムライン' },
    { id: 'trends', label: 'トレンド' },
    { id: 'all', label: 'すべて' }
  ];

  return `
    <nav class="top-tabs" aria-label="feed tabs">
      ${tabs.map((tab) => `
        <button class="top-tab ${state.activeTab === tab.id ? 'active' : ''}" data-tab="${tab.id}">${tab.label}</button>
      `).join('')}
    </nav>
  `;
}

function renderMainHeader(): string {
  return `
    <header class="timeline-header">
      <div class="header-title-row">
        <button class="mobile-menu-button" data-action="open-mobile-menu" aria-label="メニューを開く">☰</button>
        <div class="header-title-cluster">
          ${renderAppIcon('header-app-icon', 'ヤジュッター アイコン')}
          <h1>${state.activeTab === 'timeline' ? 'ホーム' : state.activeTab === 'trends' ? 'トレンド' : 'すべて'}</h1>
        </div>
        <button class="icon-button" data-action="refresh" aria-label="更新" ${state.loading ? 'disabled' : ''}>↻</button>
      </div>
      ${renderTopTabs()}
    </header>
  `;
}

function renderComposePlaceholder(): string {
  return `
    <section class="compose-box" aria-label="read only notice">
      ${renderAppIcon('avatar placeholder-avatar', 'ヤジュッター アイコン')}
      <div class="compose-main">
        <div class="fake-input">いまなにが起きてる？</div>
        <div class="compose-actions">
          <span>読む専用</span>
          <button type="button" disabled>ヤジる</button>
        </div>
      </div>
    </section>
  `;
}

function renderPendingBanner(): string {
  if (!state.pendingMeta || !state.meta) return '';
  const diff = Math.max(0, state.pendingMeta.totalCollected - state.meta.totalCollected);
  const label = diff > 0 ? `新しいヤジ ${diff.toLocaleString()}件` : '新しいヤジを表示';
  return `
    <button class="new-tweets-banner" data-action="apply-pending">
      ${label}
    </button>
  `;
}

function renderSoftNotice(meta: MetaSnapshot | null): string {
  if (!meta) return '';
  if (!meta.isPartial && !meta.isDegraded && meta.mode !== 'OVERFLOW') return '';
  return `
    <div class="soft-notice ${modeClass(meta)}">
      ${meta.isPartial ? 'コメント流速が高いため、一部取得できていない可能性があります。' : '現在、表示を軽くしています。'}
    </div>
  `;
}

function renderSearchStatus(): string {
  const query = state.query.trim();
  if (!query) return '';
  const matchedCount = getFilteredComments().length;
  return `
    <div class="search-status-bar">
      <span><b>${escapeHtml(query)}</b> を表示中</span>
      <span>${formatCompactNumber(matchedCount)}件</span>
      <button type="button" data-action="clear-search">検索を解除</button>
    </div>
  `;
}

function avatarFor(item: CommentItem): string {
  if (item.authorProfileImageUrl) {
    return `<img class="avatar" src="${escapeHtml(item.authorProfileImageUrl)}" alt="" loading="lazy" referrerpolicy="no-referrer" />`;
  }
  const initial = item.authorName?.slice(0, 1) || '匿';
  return `<div class="avatar fallback-avatar">${escapeHtml(initial)}</div>`;
}

function replyCard(reply: NonNullable<CommentItem['replies']>[number], query = ''): string {
  return `
    <article class="reply-tweet" data-reply-id="${escapeHtml(reply.id)}">
      <div class="reply-rail" aria-hidden="true"></div>
      <div class="reply-avatar">${reply.authorProfileImageUrl
        ? `<img class="avatar small-avatar" src="${escapeHtml(reply.authorProfileImageUrl)}" alt="" loading="lazy" referrerpolicy="no-referrer" />`
        : `<div class="avatar small-avatar fallback-avatar">${escapeHtml(reply.authorName?.slice(0, 1) || '匿')}</div>`}
      </div>
      <div class="reply-content">
        <header class="tweet-header">
          <strong>${escapeHtml(reply.authorName || '匿名')}</strong>
          <span>${escapeHtml(buildPseudoHandle(reply.authorName || 'anonymous'))}</span>
          <span>·</span>
          <time title="${escapeHtml(formatTime(reply.publishedAt))}">${relativeTime(reply.publishedAt)}</time>
        </header>
        <p class="tweet-text reply-text">${highlight(reply.text, query)}</p>
        <footer class="reply-actions">
          <span>♡ ${formatCompactNumber(reply.likeCount)}</span>
          ${reply.sourceUrl ? `<a href="${escapeHtml(reply.sourceUrl)}" target="_blank" rel="noreferrer">YouTube</a>` : ''}
        </footer>
      </div>
    </article>
  `;
}

function renderReplies(item: CommentItem, query = ''): string {
  if (!state.expandedReplyIds.has(item.id)) return '';
  const replies = item.replies ?? [];
  if (replies.length === 0) {
    return `
      <div class="replies-panel empty-replies">
        <p>返信は ${formatCompactNumber(item.replyCount)} 件ありますが、snapshotにはまだ含まれていません。</p>
        ${item.sourceUrl ? `<a href="${escapeHtml(item.sourceUrl)}" target="_blank" rel="noreferrer">YouTubeで返信を見る</a>` : ''}
      </div>
    `;
  }
  const hiddenCount = Math.max(0, item.replyCount - replies.length);
  return `
    <div class="replies-panel">
      ${replies.map((reply) => replyCard(reply, query)).join('')}
      ${hiddenCount > 0 ? `<p class="more-replies">ほか ${formatCompactNumber(hiddenCount)} 件の返信があります。${item.sourceUrl ? `<a href="${escapeHtml(item.sourceUrl)}" target="_blank" rel="noreferrer">YouTubeで見る</a>` : ''}</p>` : ''}
    </div>
  `;
}

function commentCard(item: CommentItem, query = ''): string {
  const topics = item.topics?.slice(0, 3).map((topic) => `<button class="topic-link" data-search-topic="${escapeHtml(topic)}">#${escapeHtml(topic)}</button>`).join('') ?? '';
  const handle = buildPseudoHandle(item.authorName || 'anonymous');
  const hasReplies = item.replyCount > 0 || (item.replies?.length ?? 0) > 0;
  const repliesOpen = state.expandedReplyIds.has(item.id);
  return `
    <article class="tweet" data-comment-id="${escapeHtml(item.id)}">
      <div class="tweet-avatar">${avatarFor(item)}</div>
      <div class="tweet-content">
        <header class="tweet-header">
          <strong>${escapeHtml(item.authorName || '匿名')}</strong>
          <span>${escapeHtml(handle)}</span>
          <span>·</span>
          <time title="${escapeHtml(formatTime(item.publishedAt))}">${relativeTime(item.publishedAt)}</time>
        </header>
        <p class="tweet-text">${highlight(item.text, query)}</p>
        ${topics ? `<div class="tweet-topics">${topics}</div>` : ''}
        <footer class="tweet-actions">
          <button class="tweet-action-button reply-toggle" data-action="toggle-replies" data-comment-id="${escapeHtml(item.id)}" ${hasReplies ? '' : 'disabled'} title="返信">
            ↩ ${formatCompactNumber(item.replyCount)}
          </button>
          <span title="リツイート風">⟲ ${formatCompactNumber(Math.max(0, Math.floor((item.replyCount + (item.score ?? 0)) / 12)))}</span>
          <span title="いいね">♡ ${formatCompactNumber(item.likeCount)}</span>
          ${item.sourceUrl ? `<a href="${escapeHtml(item.sourceUrl)}" target="_blank" rel="noreferrer">↗</a>` : '<span></span>'}
        </footer>
        ${hasReplies ? `<button class="reply-expand-link" data-action="toggle-replies" data-comment-id="${escapeHtml(item.id)}">${repliesOpen ? '返信を閉じる' : `返信を表示${item.replies?.length ? `（${item.replies.length}件取得済み）` : ''}`}</button>` : ''}
        ${renderReplies(item, query)}
      </div>
    </article>
  `;
}

function getFilteredComments(): CommentItem[] {
  const query = normalizeQuery(state.query);
  const items = state.comments?.items ?? [];
  if (!query) return items;
  const ids = new Set(
    (state.searchIndex?.items ?? [])
      .filter((item) => item.normalizedText.includes(query) || normalizeQuery(item.authorName).includes(query))
      .map((item) => item.id)
  );
  return items.filter((item) => ids.has(item.id) || normalizeQuery(commentSearchText(item)).includes(query));
}

function renderTimeline(): string {
  const query = state.query;
  const normalized = normalizeQuery(query);
  const items = (state.timeline?.items ?? []).filter((item) => {
    if (!normalized) return true;
    return normalizeQuery(commentSearchText(item)).includes(normalized);
  });
  if (items.length === 0) return `<div class="empty-state">タイムラインに表示できるヤジがありません。</div>`;
  return `<div class="feed-list">${items.map((item) => commentCard(item, query)).join('')}</div>`;
}

function trendCard(topic: TrendTopic, index: number): string {
  return `
    <article class="trend-page-card">
      <button class="trend-title-row" data-search-topic="${escapeHtml(topic.label)}">
        <span class="trend-rank">${index + 1}</span>
        <span>
          <small>日本のトレンド</small>
          <strong>#${escapeHtml(topic.label)}</strong>
          <em>${topic.count1m.toLocaleString()}件 / 1分</em>
        </span>
      </button>
      <div class="trend-samples">
        ${topic.sampleComments.slice(0, 2).map((item) => commentCard(item, topic.label)).join('')}
      </div>
    </article>
  `;
}

function renderTrends(): string {
  const query = normalizeQuery(state.query);
  const topics = (state.trends?.topics ?? []).filter((topic) => !query || normalizeQuery(topic.label).includes(query));
  if (topics.length === 0) return `<div class="empty-state">トレンドがまだありません。</div>`;
  return `<div class="trend-page-list">${topics.map(trendCard).join('')}</div>`;
}

function renderAll(): string {
  const items = getFilteredComments();
  if (items.length === 0) return `<div class="empty-state">条件に合うヤジがありません。</div>`;
  return `
    <div class="subtle-note">取得済みコメントを新着順に表示しています。YouTube上の全件保証ではありません。</div>
    <div class="feed-list">${items.slice(0, 800).map((item) => commentCard(item, state.query)).join('')}</div>
  `;
}

function renderContent(): string {
  if (state.loading && !state.meta) return `<div class="empty-state">読み込み中...</div>`;
  if (state.error) return `<div class="error-state">${escapeHtml(state.error)}</div>`;
  switch (state.activeTab) {
    case 'timeline': return renderTimeline();
    case 'trends': return renderTrends();
    case 'all': return renderAll();
  }
}

function renderSearchBox(context: 'desktop' | 'mobile' = 'desktop'): string {
  const hasCommittedQuery = normalizeQuery(state.query).length > 0;
  return `
    <section class="right-card search-card ${context === 'mobile' ? 'mobile-search-card' : ''}">
      <form class="search-form" data-action="submit-search" role="search">
        <label>
          <span aria-hidden="true">⌕</span>
          <input class="query-input" type="search" inputmode="search" autocomplete="off" enterkeyhint="search" placeholder="ヤジュッターを検索" value="${escapeHtml(state.searchDraft)}" />
        </label>
        <button class="search-submit-button" type="submit">検索</button>
        ${hasCommittedQuery ? `<button class="search-clear-button" type="button" data-action="clear-search" aria-label="検索を解除">×</button>` : ''}
      </form>
    </section>
  `;
}

function renderRightTrends(): string {
  const topics = state.trends?.topics?.slice(0, 8) ?? [];
  return `
    <section class="right-card trends-card">
      <h2>いまどうしてる？</h2>
      ${topics.length === 0
        ? '<p class="right-muted">トレンドを読み込み中です。</p>'
        : topics.map((topic) => `
          <button class="right-trend" data-search-topic="${escapeHtml(topic.label)}">
            <small>トレンド</small>
            <strong>#${escapeHtml(topic.label)}</strong>
            <span>${topic.count1m.toLocaleString()}件 / 1分</span>
          </button>
        `).join('')}
    </section>
  `;
}

function renderOpsDetails(meta: MetaSnapshot | null): string {
  if (!meta) {
    return `
      <section class="right-card ops-card">
        <details>
          <summary>運行情報</summary>
          <p>snapshot 読み込み中です。</p>
        </details>
      </section>
    `;
  }

  return `
    <section class="right-card ops-card ${modeClass(meta)}">
      <details>
        <summary>
          <span>運行情報</span>
          <b>${modeLabel(meta)}</b>
        </summary>
        <dl>
          <div><dt>更新</dt><dd>${formatTime(meta.updatedAt)}</dd></div>
          <div><dt>推定流速</dt><dd>${Math.round(meta.estimatedCommentsPerMinute).toLocaleString()}件/分</dd></div>
          <div><dt>取得間隔</dt><dd>${meta.intervalSeconds}秒</dd></div>
          <div><dt>取得ページ</dt><dd>${meta.pagesFetched}/${meta.maxPages}</dd></div>
          <div><dt>取得済み</dt><dd>${formatCompactNumber(meta.totalCollected)}</dd></div>
          <div><dt>quota残</dt><dd>${formatCompactNumber(meta.quotaRemaining)}</dd></div>
          <div><dt>配信元</dt><dd>${escapeHtml(getDataBaseUrl())}</dd></div>
        </dl>
        <p>${escapeHtml(meta.message)}</p>
      </details>
    </section>
  `;
}

function renderRightRail(): string {
  return `
    <aside class="right-rail">
      ${renderSearchBox()}
      ${renderRightTrends()}
      ${renderOpsDetails(state.meta)}
    </aside>
  `;
}

function renderMobileNav(): string {
  const navItems: Array<{ id: Tab; label: string; icon: string }> = [
    { id: 'timeline', label: 'ホーム', icon: '⌂' },
    { id: 'trends', label: 'トレンド', icon: '#' },
    { id: 'all', label: 'すべて', icon: '◎' }
  ];
  return `
    <nav class="mobile-nav" aria-label="mobile navigation">
      ${navItems.map((item) => `
        <button class="mobile-nav-item ${state.activeTab === item.id ? 'active' : ''}" data-tab="${item.id}">
          <span>${item.icon}</span>
          <small>${item.label}</small>
        </button>
      `).join('')}
      <button class="mobile-nav-item" data-action="open-mobile-menu">
        <span>⌕</span>
        <small>検索</small>
      </button>
    </nav>
  `;
}

function renderMobileDrawer(): string {
  if (!state.mobileMenuOpen) return '';
  return `
    <div class="mobile-drawer-backdrop" data-action="close-mobile-menu"></div>
    <aside class="mobile-drawer" aria-label="mobile menu">
      <header class="mobile-drawer-header">
        <strong>メニュー</strong>
        <button class="icon-button" data-action="close-mobile-menu" aria-label="閉じる">×</button>
      </header>
      ${renderSearchBox('mobile')}
      <section class="mobile-quick-tabs">
        <button data-tab="timeline" class="${state.activeTab === 'timeline' ? 'active' : ''}">ホーム</button>
        <button data-tab="trends" class="${state.activeTab === 'trends' ? 'active' : ''}">トレンド</button>
        <button data-tab="all" class="${state.activeTab === 'all' ? 'active' : ''}">すべて</button>
      </section>
      ${renderRightTrends()}
      ${renderOpsDetails(state.meta)}
      <a class="mobile-youtube-link" href="${escapeHtml(state.meta?.videoUrl ?? '#')}" ${state.meta ? 'target="_blank" rel="noreferrer"' : 'aria-disabled="true"'}>YouTubeで見る</a>
    </aside>
  `;
}

function renderCenterBody(): string {
  return `
    ${renderSoftNotice(state.meta)}
    ${renderSearchStatus()}
    ${renderPendingBanner()}
    ${state.activeTab === 'timeline' ? renderComposePlaceholder() : ''}
    ${renderContent()}
  `;
}

function render(): void {
  app.innerHTML = `
    <div class="twitter-shell">
      <div id="pull-indicator" class="pull-indicator">離すと更新</div>
      ${renderLeftRail()}
      <main class="center-column">
        ${renderMainHeader()}
        <div id="center-body">${renderCenterBody()}</div>
      </main>
      ${renderRightRail()}
      ${renderMobileNav()}
      ${renderMobileDrawer()}
    </div>
  `;
  bindEvents();
}

function syncQueryInputs(): void {
  document.querySelectorAll<HTMLInputElement>('.query-input').forEach((input) => {
    if (document.activeElement !== input) input.value = state.searchDraft;
  });
}

function renderDynamicSections(): void {
  const centerBody = document.querySelector<HTMLDivElement>('#center-body');
  if (centerBody) centerBody.innerHTML = renderCenterBody();
  syncQueryInputs();
  bindDynamicEvents();
}

function commitSearch(): void {
  state.query = state.searchDraft.trim();
  state.activeTab = state.query ? 'all' : state.activeTab;
  state.mobileMenuOpen = false;
  render();
}

function clearSearch(): void {
  state.query = '';
  state.searchDraft = '';
  state.mobileMenuOpen = false;
  render();
}

function bindDynamicEvents(): void {
  document.querySelectorAll<HTMLButtonElement>('[data-action="apply-pending"]').forEach((button) => {
    button.addEventListener('click', () => {
      void loadSnapshots(false, true, state.pendingMeta ?? undefined);
    });
  });

  document.querySelectorAll<HTMLButtonElement>('[data-search-topic]').forEach((button) => {
    button.addEventListener('click', () => {
      const topic = button.dataset.searchTopic ?? '';
      state.searchDraft = topic;
      state.query = topic;
      state.activeTab = 'all';
      state.mobileMenuOpen = false;
      render();
    });
  });

  document.querySelectorAll<HTMLButtonElement>('[data-action="toggle-replies"]').forEach((button) => {
    button.addEventListener('click', () => {
      const commentId = button.dataset.commentId;
      if (!commentId) return;
      if (state.expandedReplyIds.has(commentId)) state.expandedReplyIds.delete(commentId);
      else state.expandedReplyIds.add(commentId);
      renderDynamicSections();
    });
  });

  document.querySelectorAll<HTMLButtonElement>('[data-action="clear-search"]').forEach((button) => {
    button.addEventListener('click', () => {
      clearSearch();
    });
  });
}

function bindQueryInputs(): void {
  document.querySelectorAll<HTMLInputElement>('.query-input').forEach((input) => {
    input.addEventListener('compositionstart', () => {
      queryCompositionActive = true;
    });
    input.addEventListener('compositionend', (event) => {
      queryCompositionActive = false;
      state.searchDraft = (event.currentTarget as HTMLInputElement).value;
    });
    input.addEventListener('input', (event) => {
      state.searchDraft = (event.currentTarget as HTMLInputElement).value;
    });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        if (queryCompositionActive || event.isComposing) return;
        event.preventDefault();
        state.searchDraft = input.value;
        commitSearch();
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        clearSearch();
      }
    });
  });
}

function bindEvents(): void {
  document.querySelectorAll<HTMLElement>('[data-tab]').forEach((element) => {
    element.addEventListener('click', (event) => {
      event.preventDefault();
      state.activeTab = element.dataset.tab as Tab;
      state.mobileMenuOpen = false;
      render();
    });
  });

  document.querySelectorAll<HTMLButtonElement>('[data-action="refresh"]').forEach((button) => {
    button.addEventListener('click', () => {
      void loadSnapshots(true);
    });
  });

  document.querySelectorAll<HTMLButtonElement>('[data-action="open-mobile-menu"]').forEach((button) => {
    button.addEventListener('click', () => {
      state.mobileMenuOpen = true;
      render();
      window.setTimeout(() => document.querySelector<HTMLInputElement>('.mobile-drawer .query-input')?.focus(), 0);
    });
  });

  document.querySelectorAll<HTMLElement>('[data-action="close-mobile-menu"]').forEach((element) => {
    element.addEventListener('click', () => {
      state.mobileMenuOpen = false;
      render();
    });
  });

  document.querySelectorAll<HTMLFormElement>('[data-action="submit-search"]').forEach((form) => {
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const input = form.querySelector<HTMLInputElement>('.query-input');
      state.searchDraft = input?.value ?? state.searchDraft;
      commitSearch();
    });
  });

  document.querySelectorAll<HTMLButtonElement>('[data-action="clear-search"]').forEach((button) => {
    button.addEventListener('click', () => {
      clearSearch();
    });
  });

  bindDynamicEvents();
  bindQueryInputs();
}

async function loadSnapshots(force = false, scrollTop = false, knownMeta?: MetaSnapshot): Promise<void> {
  if (state.loading) return;
  state.loading = true;
  state.error = null;
  render();
  try {
    const snapshots = await fetchAllSnapshots({ force, meta: knownMeta });
    state.meta = snapshots.meta;
    state.timeline = snapshots.timeline;
    state.trends = snapshots.trends;
    state.comments = snapshots.comments;
    state.searchIndex = snapshots.searchIndex;
    state.pendingMeta = null;
    if (scrollTop) window.scrollTo({ top: 0, behavior: 'smooth' });
  } catch (error) {
    state.error = error instanceof Error ? error.message : 'snapshotの読み込みに失敗しました';
  } finally {
    state.loading = false;
    render();
  }
}

async function pollMeta(): Promise<void> {
  if (!state.meta || state.loading) return;
  if (document.visibilityState === 'hidden') return;
  try {
    const meta = await fetchMeta();
    if (meta.updatedAt !== state.meta.updatedAt) {
      state.pendingMeta = meta;
      render();
    }
  } catch {
    // スパイク時の閲覧継続を優先。poll失敗はメインUIに出さない。
  }
}

let touchStartY = 0;
let pullDistance = 0;
function setupPullToRefresh(): void {
  window.addEventListener('touchstart', (event) => {
    if (window.scrollY === 0) {
      touchStartY = event.touches[0]?.clientY ?? 0;
      pullDistance = 0;
    }
  }, { passive: true });

  window.addEventListener('touchmove', (event) => {
    if (window.scrollY !== 0 || touchStartY === 0) return;
    const currentY = event.touches[0]?.clientY ?? 0;
    pullDistance = Math.max(0, currentY - touchStartY);
    const indicator = document.querySelector<HTMLDivElement>('#pull-indicator');
    if (indicator) {
      indicator.style.transform = `translate(-50%, ${Math.min(72, pullDistance)}px)`;
      indicator.classList.toggle('visible', pullDistance > 40);
    }
  }, { passive: true });

  window.addEventListener('touchend', () => {
    const indicator = document.querySelector<HTMLDivElement>('#pull-indicator');
    if (indicator) {
      indicator.style.transform = 'translate(-50%, 0)';
      indicator.classList.remove('visible');
    }
    if (pullDistance > 72) void loadSnapshots(true, true);
    touchStartY = 0;
    pullDistance = 0;
  }, { passive: true });
}

setupPullToRefresh();
void loadSnapshots();
window.setInterval(() => void pollMeta(), POLL_MS);
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') void pollMeta();
});


function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator) || !import.meta.env.PROD) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      // PWA登録失敗は閲覧継続を優先して握りつぶす。
    });
  });
}

registerServiceWorker();
